import { Colors } from '@kvika/audur-theme'
import React, { useEffect, useState } from 'react'

import AudurTooltip from '../audurTooltip/AudurTooltip'
import { formatNumberIS, stringToInt } from '../../utils/Utils'
import { NumberFormatCurrencyInput } from '../AudurBaseInput'

import styles from './ExtendedSlider.module.scss'

type ExtendedSliderProps = {
	id?: string
	value: number
	valueSuffix?: string
	min: number
	max: number
	label: string
	tooltip?: string
	onChange: (value: number) => void
	secondaryValue?: string
	step: number
	minLabelFunction?: (value: number) => string
	maxLabelFunction?: (value: number) => string
	middleLabelText?: string
	middleLabelTooltip?: string
	disabled?: boolean
	disableManualInput?: boolean
}

const ExtendedSlider = (props: ExtendedSliderProps) => {
	const { value, min, max, label, onChange, secondaryValue, tooltip, id, step, disabled, disableManualInput } = props

	const { minLabelFunction, maxLabelFunction, middleLabelText, middleLabelTooltip, valueSuffix, ...rest } = props

	const [localValue, setLocalValue] = useState(value)

	useEffect(() => {
		setLocalValue(value)
	}, [value])

	const percentage = React.useMemo(() => {
		return ((value - min) / (max - min)) * 100
	}, [max, min, value])

	return (
		<div className={styles.container} id={id}>
			<div className={styles.title}>
				<div>
					{label}{' '}
					{tooltip && (
						<AudurTooltip
							text={tooltip}
							infoOutlinedColor={Colors.LightGray}
							activeInfoOutlinedColor={Colors.Silver}
							backgroundColor={Colors.Silver}
							hideCloseIcon
							hideTitleRow
						/>
					)}
				</div>
				<div>{secondaryValue}</div>
			</div>
			<NumberFormatCurrencyInput
				value={localValue}
				onChange={(event) => {
					const newVal = Number(event.target.value)
					setLocalValue(newVal)
				}}
				onBlur={() => {
					if (localValue > max) {
						onChange(max)
						setLocalValue(max)
					} else if (localValue < min) {
						onChange(min)
						setLocalValue(min)
					} else {
						onChange(localValue)
						setLocalValue(localValue)
					}
				}}
				suffix={` ${valueSuffix}`}
				min={min}
				max={max}
				label={undefined}
				name=''
				width=''
				getIsAllowedValue={() => true}
				className={styles.textInput}
				id={`number-input-${id}`}
				disabled={disabled || disableManualInput}
			/>
			<input
				{...rest}
				className={[styles.slider, disabled ? styles.disabledSlider : ''].join(' ')}
				value={value}
				min={min}
				max={max}
				style={{
					background: disabled
						? ''
						: `linear-gradient(to right, ${Colors.Orange} 0%, ${Colors.Orange} ${percentage}%, ${Colors.LightGray} ${percentage}%, ${Colors.LightGray} 100%)`,
				}}
				type='range'
				id={`extended-slider-${id}`}
				step={step}
				onChange={(event) => {
					const rawValue = stringToInt(event.target.value)

					// Calculate the maximum step value below or equal to max
					const maxStepValue = Math.floor(max / step) * step

					// If the raw value is at or past the max step value
					if (rawValue >= maxStepValue) {
						// Use the actual max value instead of just the step-aligned value
						onChange(max)
					} else {
						// Handle normally
						onChange(rawValue)
					}
				}}
				disabled={disabled}
			/>
			<div className={styles.footer}>
				<div>{(minLabelFunction && minLabelFunction(min)) ?? formatNumberIS(min)}</div>
				{middleLabelText && (
					<div className={styles.middleLabel}>
						{middleLabelText}
						{middleLabelTooltip && (
							<AudurTooltip
								text={middleLabelTooltip}
								infoOutlinedColor={Colors.LightGray}
								activeInfoOutlinedColor={Colors.Silver}
								backgroundColor={Colors.Silver}
								hideCloseIcon
								hideTitleRow
							/>
						)}
					</div>
				)}
				<div>{(maxLabelFunction && maxLabelFunction(max)) ?? formatNumberIS(max)}</div>
			</div>
		</div>
	)
}

export default ExtendedSlider
